import { node, icon } from "~/util/dom";
import { getOphotoTimelineData, IOphotoResponse } from "~/util/providers/ophoto";
import { setGlobals, Scene, LayerManager, store } from "~/ui/ophoto-timeline/globals";
import { _ } from "~/i18n";
import { isPointInStatePolygon } from "~/util/state-polygon";
import { pointInPolygonTest } from "~/util/intersect";
import { setOphotoDate, setOphotoTimeline } from "~/state/map/state";
import { watch, watchCoordinates } from "~/state/util";
import { Unsubscribe } from "redux";
import { CONFIG } from "~/config";

const MAP_REDRAW_TIMEOUT = 700;
const NEWEST = "nejnovější";
let redrawTimeout = 0;

function prepareData(data): IOphotoResponse<Array<Array<Array<number>>>> {
	const convertGeom = [];

	data.geom.data.forEach(item => {
		if (typeof item === "string") {
			convertGeom.push(Scene.stringToWGS84(item));
		}
	});

	data.geom.data = convertGeom;

	return data;
}

type TCopyright = {[key: string]: Array<string>;}

export function getCopyright(copyrights: IOphotoResponse<any>["copyrights"]): TCopyright {
	const copyright: TCopyright = {};

	copyrights.forEach(copyrightItem => {
		const key = `${copyrightItem.minzoom || ""}-${copyrightItem.maxzoom || ""}`;
		const text = copyrightItem.text.replace("%YEAR%", new Date().getFullYear().toString());

		if (!(key in copyright)) {
			copyright[key] = [];
		}

		copyright[key].push(text);
	});

	return copyright;
}

export function initModule(global) {
	setGlobals(global);
}

export default class OphotoTimeline {
	_container: HTMLDivElement;

	_list: HTMLUListElement;

	_header: HTMLDivElement;

	_actualMapset: string;

	_data: IOphotoResponse<Array<Array<Array<number>>>>;

	_callback: (layer: any) => void;

	_copyrightCallback: (copyrights: TCopyright) => void;

	_active: boolean;

	_storeUnsubscribe: Unsubscribe;

	constructor(callback: (layer?: {
		[key: string]: any;
	}) => void, copyrightCallback: (copyrights: TCopyright) => void, actualMapset = "") {
		this._container = node("div", { className: "ui-timeline-ophoto noprint" });
		this._list = node("ul");
		this._header = node("div", { className: "header" });
		this._actualMapset = actualMapset;
		this._data = null;
		this._callback = callback;
		this._copyrightCallback = copyrightCallback;
		this._active = false;
		this._storeUnsubscribe = null;

		this._header.addEventListener("click", () => {
			this._container.classList.add("open");
			const { longitude, latitude } = store.getState().map;

			isPointInStatePolygon(longitude, latitude, {
				cs: true,
			}).then(inCS => {
				Mapy.getComponent("logger").log("click", {
					text: "ortofotoTimelineShow",
					values: this._data.timeline.map(timeline => timeline.mapset ? timeline.label : NEWEST),
					country: inCS ? "cz" : "sk",
				});
			}, () => {});
		});

		if (this._actualMapset) {
			this._setMapset(this._actualMapset, true);
		} else {
			this._load().then(() => {
				if (this._data) {
					this._copyrightCallback(getCopyright(this._data.copyrights));
				}
			}, () => {});
		}
	}

	get container() {
		return this._container;
	}

	get active() {
		return this._active;
	}

	start() {
		if (this._active) {
			return;
		}

		this._active = true;

		if (!this._storeUnsubscribe) {
			const wt = watch(store.getState, "map", watchCoordinates);

			this._storeUnsubscribe = store.subscribe(wt((newMap, oldMap) => {
				if (
					(newMap.longitude !== oldMap.longitude
					|| newMap.latitude !== oldMap.latitude
					|| newMap.zoom !== oldMap.zoom) && newMap.provider === "map"
				) {
					this._mapRedraw();
				}
			}));
		}

		if (this._actualMapset) {
			this._setMapset(this._actualMapset, true);
		}

		this._show();
	}

	stop() {
		if (!this._active) {
			return;
		}

		this._active = false;

		if (redrawTimeout) {
			clearTimeout(redrawTimeout);
		}

		this._storeUnsubscribe?.();
		this._storeUnsubscribe = null;
	}

	_load() {
		const { zoom } = store.getState().map;

		return getOphotoTimelineData(Scene.getViewport(), zoom, this._actualMapset).then(data => {
			if (this._data) {
				this._data = prepareData(data);
				this._buildList();
				this._setActiveMapset();
			} else if (data?.timeline) {
				this._data = prepareData(data);
				this._build();
			} else {
				this._data = null;
			}

			this._show();
		}, error => {
			// eslint-disable-next-line no-console
			console.log(error);

			this._data = null;
			this._show();
		});
	}

	_setActiveMapset(text?: string) {
		this._header.querySelector("span").innerText = text || this._actualMapset === "" ? _("newest") : this._data.timeline?.filter(timeline => timeline.mapset === this._actualMapset)[0]?.label || "";
	}

	_setMapset(mapset = "", force = false) {
		if (this._actualMapset !== mapset || force) {
			if (this._data?.timeline?.length) {
				this._actualMapset = mapset;
			}

			this._list?.querySelector(".active")?.classList.remove("active");
			this._list?.querySelector(`[data-mapset='${mapset || "newest"}']`)?.classList.add("active");

			this._load().then(() => {
				if (this._data && this._data.active?.mapset && this._data.active?.mapset !== this._actualMapset) {
					this._actualMapset = this._data.active?.mapset;
					this._setActiveMapset();
				}

				if (this._actualMapset) {
					const mapsetData = this._data
						? this._data.timeline?.filter(timelineItem => timelineItem.mapset === this._actualMapset)[0]
						: null;

					if (mapsetData) {
						const copyright = getCopyright(this._data.copyrights);

						this._callback({
							zoom: [mapsetData.minzoom, mapsetData.maxzoom],
							base: `${mapsetData.layers[0]}`,
							url: `${CONFIG.mapserver.url}/${mapsetData.layers[0]}/`,
							copyright,
							layers: mapsetData.layers.slice(1),
						});

						Mapy.getComponent("history").save();
					} else {
						this._setActiveMapset(_("newest"));
					}
				} else {
					LayerManager.setMapset("letecka");
				}

				store.dispatch(setOphotoTimeline(this._actualMapset));
			}, () => {
				this._show();
			});

			this._data && this._setActiveMapset();
		}
	}

	_build() {
		this._buildList();

		this._header.innerHTML = "";

		this._header.append(
			node("span", { className: "header-text" }),
			icon("dropdown"),
		);

		this._container.append(this._header, this._list);

		this._setActiveMapset();
	}

	_buildList() {
		this._list.innerHTML = "";

		const frag = document.createDocumentFragment();

		this._data.timeline.forEach(item => {
			const liItem = node("li", {}, item.mapset ? item.label : _("newest"));

			liItem.dataset.mapset = item.mapset || "newest";

			liItem.addEventListener("click", () => {
				const { longitude, latitude } = store.getState().map;

				if (item.mapset !== this._actualMapset) {
					this._setMapset(item.mapset);

					isPointInStatePolygon(longitude, latitude, {
						cs: true,
					}).then(inCS => {
						Mapy.getComponent("logger").log("click", {
							text: "ortofotoTimelineSelect",
							value: item.mapset || "",
							country: inCS ? "cz" : "sk",
						});
					}, () => {});
				}


				isPointInStatePolygon(longitude, latitude, {
					cs: true,
				}).then(inCS => {
					Mapy.getComponent("logger").log("click", {
						text: "ortofotoTimelineHide",
						values: this._data.timeline.map(timeline => timeline.mapset ? timeline.label : NEWEST),
						country: inCS ? "cz" : "sk",
					});
				}, () => {});

				this._container.classList.remove("open");
			});

			if (item.mapset) {
				this._actualMapset === item.mapset && liItem.classList.add("active");
			} else {
				const button = node("button", { type: "button" });
				const ico = icon("dropup");

				button.append(ico);

				liItem.append(button);

				button.addEventListener("click", event => {
					event.stopPropagation();

					Mapy.getComponent("logger").log("click", {
						text: "ortofotoTimelineHide",
						values: this._data.timeline.map(timeline => timeline.mapset ? timeline.label : NEWEST),
					});

					this._container.classList.remove("open");
				});

				this._actualMapset === "" && liItem.classList.add("active");
			}

			frag.append(liItem);
		});

		this._list.append(frag);
	}

	_mapRedraw() {
		if (redrawTimeout) {
			clearTimeout(redrawTimeout);
		}

		redrawTimeout = window.setTimeout(() => {
			const { longitude, latitude, zoom } = store.getState().map;

			if (!this._data || zoom > this._data.geom.maxzoom || zoom < this._data.geom.minzoom || !pointInPolygonTest([longitude, latitude], this._data.geom.data)) {
				this._setMapset(this._actualMapset, true);
			}

			this._show();
		}, MAP_REDRAW_TIMEOUT);
	}

	async _show() {
		const { longitude, latitude, zoom } = store.getState().map;
		const show = zoom >= 8 && await isPointInStatePolygon(longitude, latitude, {
			cs: true,
			sk: true,
		}) && this._data;

		if (show) {
			this._container.classList.remove("hide");
		} else {
			this._container.classList.add("hide");
		}

		this._setDate();
	}

	_setDate() {
		store.dispatch(setOphotoDate(this._data?.date || ""));
	}
}
